import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, FormProps, InputNumber, Spin, Typography } from 'antd';

// services
import { onGetTreasuryShares, onUpdateTreasuryShares } from '../services/shareholdersService';
import NotificationService from 'core/services/notification';

export interface TreasurySharesComponentRef {
	refreshTreasuryShares: () => void;
}

interface TreasurySharesComponentProps {
	editable: boolean;
	onChange: () => void;
}

const TreasurySharesComponent = forwardRef<TreasurySharesComponentRef, TreasurySharesComponentProps>(({ onChange, editable }, ref) => {
	const { t } = useTranslation();
	const [treasuryForm] = Form.useForm();

	const [isTreasuryPercentageLoading, setIsTreasuryPercentageLoading] = useState(true);
	const [isTreasuryPercentageSubmitting, setIsTreasuryPercentageSubmitting] = useState(false);
	const [maxTotalTreasury, setMaxTotalTreasury] = useState(0);

	type FieldType = {
		total_number_of_treasury_shares: string;
	};

	useEffect(() => {
		getTreasuryShares();
	}, []);

	const getTreasuryShares = async () => {
		try {
			setIsTreasuryPercentageLoading(true);
			const response = await onGetTreasuryShares();
			treasuryForm.setFieldValue('total_number_of_treasury_shares', response.total_number_of_treasury_shares);
			setMaxTotalTreasury(response.max_treasury_shares);
			setIsTreasuryPercentageLoading(false);
		} catch (error) {
			setIsTreasuryPercentageLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const updateTreasuryShares: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsTreasuryPercentageSubmitting(true);
			const response = await onUpdateTreasuryShares(+values.total_number_of_treasury_shares);
			setMaxTotalTreasury(response.max_treasury_shares);
			setIsTreasuryPercentageSubmitting(false);
			onChange();
			NotificationService.success(t('COMMON.SUCCESS'), t('SHAREHOLDERS_REGISTRY.TOTAL_TREASURY_SHARES_UPDATED'));
		} catch (error) {
			setIsTreasuryPercentageSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	useImperativeHandle(ref, () => ({
		refreshTreasuryShares: getTreasuryShares,
	}));

	return (
		<div className="containerDiv mt-4">
			<Typography.Text>{t('SHAREHOLDERS_REGISTRY.TOTAL_TREASURY_NUM')}</Typography.Text>
			<Form form={treasuryForm} onFinish={updateTreasuryShares} className="w-100">
				<Flex justify="space-between" align="center" gap={20}>
					<Spin spinning={isTreasuryPercentageLoading} size="small">
						<Form.Item
							name="total_number_of_treasury_shares"
							rules={[
								{
									type: 'number',
									max: maxTotalTreasury,
									message: t('SHAREHOLDERS_REGISTRY.MAX_TREASURY_NUM'),
								},
							]}>
							<InputNumber<number>
								min={0}
								style={{ width: 300, display: 'block' }}
								disabled={isTreasuryPercentageLoading || !editable}
								formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={(value) => parseFloat((value ?? '0').replace(/,/g, '').replace(/\s/g, ''))}
								onChange={(value) => {
									if (value && value > maxTotalTreasury) treasuryForm.setFieldValue('total_number_of_treasury_shares', maxTotalTreasury);
								}}
							/>
						</Form.Item>
					</Spin>

					{editable && (
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							loading={isTreasuryPercentageSubmitting}
							disabled={isTreasuryPercentageLoading || isTreasuryPercentageSubmitting}>
							{treasuryForm.getFieldValue('total_number_of_treasury_shares') > 0 ? t('COMMON.UPDATE') : t('COMMON.SAVE')}
						</Button>
					)}
				</Flex>
			</Form>
		</div>
	);
});

export default TreasurySharesComponent;
